import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const YOUTUBE_API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;

interface VideoDetails {
  duration: string;
  title: string;
  channelTitle: string;
}

const YouTubeInput = ({
  value,
  onChange,
  isLoading,
  onValidVideo,
}: {
  value: string;
  onChange: (value: string) => void;
  isLoading: boolean;
  onValidVideo?: (details: VideoDetails) => void;
}) => {
  const [isValid, setIsValid] = useState(false);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [videoDetails, setVideoDetails] = useState<VideoDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // YouTube URL validation regex
  const validateYouTubeUrl = (url: string) => {
    const patterns = [
      /^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
      /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([^?]+)/,
      /^https?:\/\/(?:www\.)?youtu\.be\/([^?]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }
    return null;
  };

  // Convert ISO 8601 duration to human readable format
  const formatDuration = (duration: string) => {
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    if (!match) return "Unknown duration";

    const hours = (match[1] || "").replace("H", "");
    const minutes = (match[2] || "").replace("M", "");
    const seconds = (match[3] || "").replace("S", "");

    const parts = [];
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (seconds) parts.push(`${seconds}s`);

    return parts.join(" ");
  };

  // Fetch video details from YouTube API
  const fetchVideoDetails = async (id: string) => {
    try {
      setIsLoadingDetails(true);
      setError(null);

      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=contentDetails,snippet&id=${id}&key=${YOUTUBE_API_KEY}`,
      );
      const data = await response.json();

      if (data.items && data.items.length > 0) {
        const details = {
          duration: formatDuration(data.items[0].contentDetails.duration),
          title: data.items[0].snippet.title,
          channelTitle: data.items[0].snippet.channelTitle,
        };
        setVideoDetails(details);
        onValidVideo?.(details);
      } else {
        setError("Video not found or is private");
      }
    } catch (err) {
      setError("Failed to fetch video details");
      console.error("Error fetching video details:", err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    const id = validateYouTubeUrl(value);
    setIsValid(!!id);
    setVideoId(id);
    setVideoDetails(null);

    if (id) {
      fetchVideoDetails(id);
    }
  }, [value]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter YouTube URL..."
          className={isValid ? "border-green-500" : ""}
          disabled={isLoading}
        />
        {videoId && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            disabled={isLoading}
          >
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
        )}
      </div>

      {value && !isValid && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please enter a valid YouTube URL</AlertDescription>
        </Alert>
      )}

      {isLoadingDetails && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="h-4 w-4 animate-spin" />
          <span>Loading video details...</span>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {videoDetails && (
        <div className="flex flex-col gap-2 p-4 bg-muted rounded-lg">
          <p className="font-medium">{videoDetails.title}</p>
          <div className="flex gap-2">
            <Badge variant="outline">{videoDetails.channelTitle}</Badge>
            <Badge variant="secondary">
              <Clock className="h-4 w-4 mr-1" />
              {videoDetails.duration}
            </Badge>
          </div>
        </div>
      )}

      {videoId && showPreview && (
        <div className="relative pt-[56.25%] w-full">
          <iframe
            className="absolute top-0 left-0 w-full h-full rounded-lg"
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video preview"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
};

export default YouTubeInput;
